import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "../lib/api";

const projectTypes = ["Wedding / Event", "Corporate / Real Estate", "Editorial / Brand", "Aerial / Drone", "Multi-day Commission", "Something Else"];
const budgets = ["Under $1,500", "$1,500 – $5,000", "$5,000 – $15,000", "$15,000 +", "Prefer not to say"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_type: projectTypes[0], budget: budgets[0], date: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/consultation", form);
      setSent(true);
      toast.success("Received. The studio will reply within 48 hours.");
    } catch (err) {
      toast.error("Submission failed. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <main className="pt-24 md:pt-28 min-h-screen" data-testid="page-contact">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="md:col-span-5"
        >
          <div className="h-eyebrow mb-6">§ Private Consultation</div>
          <h1 className="h-display text-5xl md:text-7xl leading-[0.95]">
            Discuss<br/>
            <span className="italic font-serif-editorial text-[var(--gold)] font-light">your vision.</span>
          </h1>
          <p className="font-serif-editorial text-lg md:text-xl leading-relaxed text-[var(--ink)]/80 mt-10 max-w-md">
            The studio accepts a limited number of commissions each quarter. Send the outline of your project &mdash; we reply within 48 hours, in full, in writing.
          </p>
          <div className="mt-16 space-y-6 font-serif-editorial text-lg text-[var(--ink)]/70">
            <div><span className="h-eyebrow text-[var(--ink-muted)] block mb-1">Direct</span><a className="text-[var(--gold)] hover:underline" href="mailto:ian@sebastianvisualcorp.ca">ian@sebastianvisualcorp.ca</a></div>
            <div><span className="h-eyebrow text-[var(--ink-muted)] block mb-1">Territory</span>Surrey, BC &mdash; Available across Canada</div>
            <div><span className="h-eyebrow text-[var(--ink-muted)] block mb-1">Response</span>Within 48 hours, without exception</div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="md:col-span-7"
        >
          {sent ? (
            <div className="border border-[var(--gold)]/40 p-10 md:p-14" data-testid="contact-success">
              <div className="numeral text-6xl md:text-7xl">✦</div>
              <h2 className="h-display text-3xl md:text-5xl mt-6">Received.</h2>
              <p className="font-serif-editorial italic text-lg md:text-xl text-[var(--ink)]/80 mt-6 leading-relaxed">
                Your outline is now on the studio's desk. A written reply — with scope, timeline and next steps — will reach you within 48 hours.
              </p>
              <button onClick={() => { setSent(false); setForm({ name: "", email: "", phone: "", project_type: projectTypes[0], budget: budgets[0], date: "", message: "" }); }} className="mt-10 font-sans-display uppercase tracking-[0.3em] text-[11px] text-[var(--gold)] border-b border-[var(--gold)]/40 pb-2 hover:border-[var(--gold)]" data-testid="contact-send-another">Send another →</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-8" data-testid="consultation-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <label className="block">
                  <span className="h-eyebrow block mb-2">Full Name</span>
                  <input className="line-input" required value={form.name} onChange={set("name")} placeholder="Your name" data-testid="contact-name" />
                </label>
                <label className="block">
                  <span className="h-eyebrow block mb-2">Email</span>
                  <input type="email" className="line-input" required value={form.email} onChange={set("email")} placeholder="you@studio.com" data-testid="contact-email" />
                </label>
                <label className="block">
                  <span className="h-eyebrow block mb-2">Phone (optional)</span>
                  <input className="line-input" value={form.phone} onChange={set("phone")} placeholder="+1 …" data-testid="contact-phone" />
                </label>
                <label className="block">
                  <span className="h-eyebrow block mb-2">Preferred Date</span>
                  <input type="date" className="line-input" value={form.date} onChange={set("date")} data-testid="contact-date" />
                </label>
                <label className="block">
                  <span className="h-eyebrow block mb-2">Project Type</span>
                  <select className="line-input" value={form.project_type} onChange={set("project_type")} data-testid="contact-project-type">
                    {projectTypes.map((t) => <option key={t} value={t} className="bg-[var(--bg)]">{t}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="h-eyebrow block mb-2">Budget</span>
                  <select className="line-input" value={form.budget} onChange={set("budget")} data-testid="contact-budget">
                    {budgets.map((t) => <option key={t} value={t} className="bg-[var(--bg)]">{t}</option>)}
                  </select>
                </label>
              </div>

              <label className="block">
                <span className="h-eyebrow block mb-2">The Outline</span>
                <textarea className="line-input" required rows={6} value={form.message} onChange={set("message")} placeholder="Where, when, and what you want the world to feel when they see it." data-testid="contact-message" />
              </label>

              <div className="pt-4 flex flex-col md:flex-row md:items-center gap-6">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="inline-flex items-center gap-3 bg-[var(--royal)] hover:bg-[var(--royal-soft)] text-white px-10 py-4 font-sans-display uppercase tracking-[0.3em] text-[11px] transition-colors disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Request a Private Consultation →"}
                </button>
                <div className="font-serif-editorial italic text-[var(--ink-muted)] text-sm">Response within 48 hours.</div>
              </div>
            </form>
          )}
        </motion.div>
      </section>

      <div className="h-16 md:h-24" />
    </main>
  );
}
